import type {
  AppointmentWithRelations,
  BatchProviderRanking,
  RankedProvider,
  TaskWithRelations,
} from '@vagusx/clients';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AppointmentTypeResults } from '@/components/custom/appointment-type-results';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime } from '@/lib/format';
import { useAppointmentTypes } from '@/hooks/use-appointment-types';
import { useProviders } from '@/hooks/use-providers';
import {
  useSimulateAppointmentTypeForTask,
  useProviderSelectionSettings,
} from '@/hooks/use-appointment-type-matching';
import {
  useReferralAppointment,
  referralStatusForTask,
  REFERRAL_STATUS_LABEL,
  formatUrgency,
  type ReferralUrgency,
} from '@/hooks/use-referrals';
import { useTaskReferralDocument } from '@/pages/tasks/overview/use-task-referral';

/**
 * Lazy per-row cells for the Referrals table. Each cell mounts the hooks it
 * needs and renders a shimmer while loading — so the table paints immediately
 * and the per-row document / appointment lookups stream in for only the rows
 * currently on screen.
 *
 * The scheduled-time column shows the ACTUAL booked appointment (resolved via
 * the referral task's linked appointment) and renders blank until the referral
 * is scheduled. `AppointmentTypeCell` and `ProvidersCell` are the exceptions:
 * both fall back to the batch ranking for rows that aren't booked yet — the type
 * scheduling would pick, and the ranked provider pool — so the two columns
 * describe the same top suggestion for the whole worklist.
 */

function CellSkeleton(): React.ReactElement {
  return <Skeleton className="h-4 w-20" />;
}

function Dash(): React.ReactElement {
  return <span className="text-muted-foreground">—</span>;
}

export function ReferredByCell({ task }: { task: TaskWithRelations }): React.ReactElement {
  const { document, isLoading } = useTaskReferralDocument(task);
  if (isLoading) return <CellSkeleton />;
  const provider = document?.referringProviderName?.trim();
  const practice = document?.referringPracticeName?.trim();
  if (!provider && !practice) return <Dash />;
  return (
    <div className="flex min-w-0 flex-col">
      <span className="truncate font-medium" title={provider ?? practice}>
        {provider ?? practice}
      </span>
      {provider && practice && (
        <span className="truncate text-xs text-muted-foreground" title={practice}>
          {practice}
        </span>
      )}
    </div>
  );
}

export function DateReferredCell({ task }: { task: TaskWithRelations }): React.ReactElement {
  const { document, isLoading } = useTaskReferralDocument(task);
  if (isLoading) return <CellSkeleton />;
  // Prefer the document's own date; fall back to when the referral landed
  // in our system (task creation).
  const date = document?.documentDate ?? document?.createdAt ?? task.createdAt;
  return <span>{formatDate(date)}</span>;
}

const URGENCY_VARIANT: Record<ReferralUrgency, 'destructive' | 'secondary' | 'outline'> = {
  Stat: 'destructive',
  Urgent: 'secondary',
  Regular: 'outline',
};

export function UrgencyCell({ task }: { task: TaskWithRelations }): React.ReactElement {
  const { document, isLoading } = useTaskReferralDocument(task);
  if (isLoading) return <CellSkeleton />;
  const urgency = formatUrgency(document?.urgency, task.labels, task.priority);
  return <Badge variant={URGENCY_VARIANT[urgency]}>{urgency}</Badge>;
}

/**
 * Status: whether the referral has a booked appointment yet. Derived purely
 * from the task↔appointment link, so it needs no per-row fetch.
 */
export function StatusCell({ task }: { task: TaskWithRelations }): React.ReactElement {
  const status = referralStatusForTask(task);
  return (
    <Badge variant={status === 'scheduled' ? 'default' : 'outline'}>
      {REFERRAL_STATUS_LABEL[status]}
    </Badge>
  );
}

/**
 * Shared shell for the actual-booked-appointment columns. Centralizes the
 * "blank until booked, shimmer while loading" gating so the three cells below
 * only describe what to render once an appointment is in hand. `extraLoading`
 * lets a cell hold the shimmer for a second dependency (e.g. the appointment-
 * type catalog) so it doesn't flash blank before that resolves.
 */
function BookedAppointmentCell({
  task,
  extraLoading = false,
  render,
}: {
  task: TaskWithRelations;
  extraLoading?: boolean;
  render: (appointment: AppointmentWithRelations) => React.ReactElement;
}): React.ReactElement {
  const { appointment, isLoading } = useReferralAppointment(task);
  if (!task.appointmentId) return <Dash />;
  if (isLoading || extraLoading) return <CellSkeleton />;
  if (!appointment) return <Dash />;
  return render(appointment);
}

/**
 * Appointment type: what this referral is booked as, or — until it's booked —
 * what type scheduling would pick.
 *
 * Mirrors `ProvidersCell`: the column is meaningful for the whole worklist, not
 * just booked rows.
 *  - booked   → the type on the actual appointment, resolved from the org's type
 *               catalog. Settled fact, rendered as the primary text.
 *  - unbooked → the type the TOP-ranked provider would be booked as, straight
 *               from the same batch ranking the Provider(s) column reads. This
 *               is the provider-scoped winner the scheduling drawer resolves, so
 *               the two columns describe the same top suggestion. Rendered muted
 *               to read as a forecast, not a commitment.
 */
export function AppointmentTypeCell({
  task,
  ranking,
  isLoading,
}: {
  task: TaskWithRelations;
  ranking: BatchProviderRanking | undefined;
  isLoading: boolean;
}): React.ReactElement {
  // The appointment carries only the type id; resolve the name from the org's
  // type catalog (cached org-wide, so this isn't a per-row request). Hold the
  // shimmer until the catalog loads so a scheduled row doesn't flash blank.
  const { data: types, isLoading: typesLoading } = useAppointmentTypes();
  const { appointment, isLoading: appointmentLoading } = useReferralAppointment(task);

  if (task.appointmentId) {
    if (appointmentLoading || typesLoading) return <CellSkeleton />;
    if (!appointment) return <Dash />;
    const name = appointment.appointmentTypeId
      ? (types ?? []).find((t) => t.id === appointment.appointmentTypeId)?.name
      : undefined;
    // Booked but the type id doesn't resolve (e.g. archived/removed type): still
    // read as scheduled with a muted label, not a blank dash that would look
    // like "not scheduled".
    if (!name) return <span className="text-muted-foreground">Unknown type</span>;
    return (
      <span className="truncate font-medium" title={name}>
        {name}
      </span>
    );
  }

  return <RankedTypeCell ranking={ranking} isLoading={isLoading} />;
}

/**
 * The type scheduling would determine for an unbooked referral: the top-ranked
 * provider's resolved type from the page batch. Null when that provider is
 * tied across types (no single answer to assert), so the cell stays a dash.
 * Shared with the campaign queue's Ready lane, whose rows are all unbooked.
 */
export function RankedTypeCell({
  ranking,
  isLoading,
}: {
  ranking: BatchProviderRanking | undefined;
  isLoading: boolean;
}): React.ReactElement {
  if (isLoading) return <CellSkeleton />;
  const forecast = ranking?.providers[0]?.appointmentTypeName;
  if (!forecast) return <Dash />;
  return (
    <span className="truncate italic text-muted-foreground" title={forecast}>
      {forecast}
    </span>
  );
}

export function ScheduledTimeCell({ task }: { task: TaskWithRelations }): React.ReactElement {
  return (
    <BookedAppointmentCell
      task={task}
      render={(appointment) => <span>{formatDateTime(appointment.startTime)}</span>}
    />
  );
}

/**
 * How the ranked pool reads in a single cell. Returns null when there is
 * nothing to show, so the caller renders its usual dash.
 *
 * The cell shows only the top-ranked provider plus a `+N` count for the rest —
 * a wide worklist column can't fit a comma-joined roster (it wrapped to two
 * lines and buried the primary), and the full ordered pool is one click away in
 * the popover. `primaryName` is always the rank-1 provider; `extraCount` is how
 * many more the pool holds; `fullLabel` is the complete ordered list, kept for
 * the trigger's hover title.
 *
 * Pure and exported for test: the ordering is the algorithm's answer, and
 * silently reordering it here would misrepresent the ranking the agent will
 * actually follow.
 */
export function formatProviderPool(ranking: BatchProviderRanking | undefined): {
  primaryName: string;
  extraCount: number;
  fullLabel: string;
  isFallback: boolean;
} | null {
  const providers = ranking?.providers ?? [];
  const [primary, ...rest] = providers;
  if (!primary) return null;
  return {
    primaryName: primary.providerName,
    extraCount: rest.length,
    fullLabel: providers.map((p) => p.providerName).join(', '),
    // A fallback pool is the org's default provider, not a clinical match.
    isFallback: providers.every((p) => p.isDefaultFallback),
  };
}

/**
 * Provider(s): who this referral is with, or — until it's booked — who it
 * SHOULD be with.
 *
 * Two different answers depending on the row's state, which is why the column
 * is plural:
 *  - booked   → the one clinician on the appointment. Settled fact.
 *  - unbooked → the ranked pool from provider selection (ADR-141), best first,
 *               comma-separated. Previously this cell was simply blank for
 *               every unscheduled row, which is most of the worklist.
 *
 * `ranking` is looked up from the page-level batch rather than fetched here:
 * per-row would re-run the org's eligibility + roster + booking-count reads
 * once per visible row.
 */
export function ProvidersCell({
  task,
  ranking,
  isLoading,
}: {
  task: TaskWithRelations;
  ranking: BatchProviderRanking | undefined;
  isLoading: boolean;
}): React.ReactElement {
  const { appointment, isLoading: appointmentLoading } = useReferralAppointment(task);
  // The appointment embeds only raw first/last name, and EHR sync packs the
  // credential into those fields — joining them yields "Celerina Celerina
  // Noreik, NP". The ranked half of this column goes through the server's
  // `displayName`, so without this the SAME clinician renders two different
  // ways in the same column. Resolve against the org roster instead (cached
  // org-wide, so not a per-row request) — same trick `AppointmentTypeCell`
  // uses for the type catalog.
  const { data: providersData } = useProviders();

  if (task.appointmentId) {
    if (appointmentLoading) return <CellSkeleton />;
    const provider = appointment?.provider;
    if (!provider) return <Dash />;
    const name =
      (providersData?.items ?? []).find((p) => p.id === provider.id)?.displayName ??
      // Deactivated or unsynced provider: the roster won't have them, and a raw
      // name still beats a dash that reads as "not scheduled".
      `${provider.firstName} ${provider.lastName}`.trim();
    if (!name) return <Dash />;
    return (
      <span className="truncate" title={name}>
        {name}
      </span>
    );
  }

  return <RankedProvidersCell taskId={task.id} ranking={ranking} isLoading={isLoading} />;
}

/**
 * The ranked pool of an unbooked referral: the top provider, `+N` for the
 * rest, and "Why these providers?" on click. Shared with the campaign queue's
 * Ready lane, whose rows are all unbooked.
 */
export function RankedProvidersCell({
  taskId,
  ranking,
  isLoading,
}: {
  /** The referral's task, for the popover's per-row explanation. */
  taskId: string;
  ranking: BatchProviderRanking | undefined;
  isLoading: boolean;
}): React.ReactElement {
  if (isLoading) return <CellSkeleton />;

  const pool = formatProviderPool(ranking);
  if (!pool) return <Dash />;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          // The row itself opens the task detail panel; this must not also
          // trigger that.
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'flex max-w-full items-center gap-1.5 text-left',
            pool.isFallback && 'italic text-muted-foreground'
          )}
          title={
            pool.isFallback
              ? `Default provider (no clinical match) — click to see why · ${pool.fullLabel}`
              : `Why these providers? · ${pool.fullLabel}`
          }
        >
          <span className="truncate underline decoration-dotted underline-offset-4 hover:decoration-solid">
            {pool.primaryName}
          </span>
          {pool.extraCount > 0 && (
            <Badge variant="secondary" className="shrink-0 px-1.5 text-xs font-normal">
              +{pool.extraCount}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      {/* Radix unmounts this when closed, so the explanation is only fetched
          for the row a user actually asks about — the whole page's ranking
          already came from the batch.

          Height is capped by Radix's own measured available height, NOT a
          fixed vh: a three-provider breakdown is taller than the room left
          beside a row near the bottom of the table, and a fixed cap let it
          flip upward and run off the top of the window — clipping the header
          and the signal chips. */}
      <PopoverContent
        align="end"
        collisionPadding={12}
        className="max-h-(--radix-popover-content-available-height) w-[28rem] overflow-y-auto"
        // Radix portals this to <body>, but React's synthetic events still
        // bubble through the COMPONENT tree — so a click on the collapsibles
        // inside would reach the table row's onRowClick and open the task
        // detail panel. Stop clicks at the content boundary; the trigger does
        // the same on its side.
        onClick={(e) => e.stopPropagation()}
      >
        <ProviderPoolReasons taskId={taskId} />
      </PopoverContent>
    </Popover>
  );
}

/**
 * "Why these providers?" for one row — the SAME breakdown the appointment-type
 * simulator renders, from the same components, so staff comparing the worklist
 * against the simulator don't read them as two different algorithms.
 *
 * Fetched per row on open rather than shipped with the page batch: the
 * contribution detail is several times the size of the names, and on a 25-row
 * page it would be paid for every row to be read on approximately one.
 */
export function ProviderPoolReasons({
  taskId,
  selectedProviderId,
  selectedLocationId,
  onSelectProvider,
}: {
  taskId: string;
  /** Highlights the provider chosen, when the host lets one be chosen. */
  selectedProviderId?: string | undefined;
  /** The chosen row's location: a provider ranked at two locations is two rows. */
  selectedLocationId?: string | undefined;
  /**
   * When set, a provider can be picked: the campaign queue's slots panel
   * shows the picked provider's open dates beside this breakdown.
   */
  onSelectProvider?: ((provider: RankedProvider) => void) | undefined;
}): React.ReactElement {
  const { data, isLoading, isError } = useSimulateAppointmentTypeForTask(taskId, true);
  const {
    data: settings,
    isLoading: settingsLoading,
    isError: settingsError,
  } = useProviderSelectionSettings();

  // Advisory panel — a failure here shouldn't produce an error toast over a
  // worklist the user is only reading. Checked BEFORE the loading branch:
  // gating the spinner on `!settings` alone would spin forever once the
  // settings query settles in error, since its `data` stays undefined. That is
  // a live failure mode for this endpoint — a stale strategy key in the stored
  // JSONB has 500'd it before (see `reconcilePrioritization`).
  if (isError || settingsError) {
    return <p className="text-sm text-muted-foreground">Couldn't load the ranking reasons.</p>;
  }

  if (isLoading || settingsLoading || !data || !settings) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Working out the ranking…
      </div>
    );
  }

  const signals = data.signals;
  const chips: string[] = [];
  if (signals.cptCodes.length > 0) chips.push(`CPT: ${signals.cptCodes.join(', ')}`);
  if (signals.icdCodes.length > 0) chips.push(`ICD: ${signals.icdCodes.join(', ')}`);
  if (signals.patientType)
    chips.push(signals.patientType === 'new' ? 'New patient' : 'Existing patient');
  if (signals.isUrgent) chips.push('Urgent');

  return (
    <div className="space-y-3">
      {/* What the match ran AGAINST — the section headings live inside
          AppointmentTypeResults, so this is just the matched-signal context.
          Without it, "no code match" reads as a bug in the ranking rather than a
          referral with no usable codes. */}
      {chips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <Badge key={chip} variant="secondary" className="text-xs font-normal">
              {chip}
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          This referral carries no codes to match on — the ranking falls back to availability and
          weight alone.
        </p>
      )}

      <AppointmentTypeResults
        result={data.recommendation}
        prioritization={settings.prioritization}
        selectedProviderId={selectedProviderId}
        selectedLocationId={selectedLocationId}
        onSelectProvider={onSelectProvider}
      />
    </div>
  );
}
